import type { Role } from './enums.js';

/**
 * Three roles:
 *  - admin: Infinity EV. Everything.
 *  - manager: one or two people at an organisation who look after its drivers and RFID
 *    cards. They see the charger window read only and can send read-only commands, but
 *    can't change chargers or prices.
 *  - user: read only.
 *
 * Every action the app can take. The API checks these, and the web app uses
 * the same list to hide buttons a person can't use.
 *
 * Scoping: admins act across all organisations. Managers and users only ever
 * see the organisations they belong to (user_organisations), and the API
 * enforces that in one place, whatever the permission.
 */
export const PERMISSIONS = [
  // Organisations and sites
  'organisations.view',
  'organisations.create',
  'organisations.edit',
  'organisations.delete',
  'sites.view',
  'sites.edit',

  // Chargers
  'chargers.view', // the list with live status
  'chargers.details', // the charger window: status, prices and command history, read only
  'chargers.query', // read-only commands: ask for a message, read the configuration
  'chargers.edit',
  'chargers.register', // accept a new charger that connected for the first time
  'chargers.command', // remote start/stop, reset, unlock, availability, clear cache
  'chargers.configure', // ChangeConfiguration, vendor profile, OCPP password

  // Drivers and RFID cards
  'drivers.view',
  'drivers.edit',
  'cards.edit',

  // Sessions, tariffs, money
  'sessions.view',
  'tariffs.edit', // prices, time-of-use schedules, RFID card rates: admins only
  'billing.view',
  'billing.run', // monthly driver billing, refunds

  // Reports
  'reports.view',
  'reports.export',

  // People and system
  'users.view',
  'users.edit',
  'settings.edit', // emission factors, system settings
  'powersensor.edit', // turn Powersensor on for an organisation, and its reimbursement rate
  'audit.view',
  'apikeys.manage', // keys for the read-only customer API
  'ocpp.logs.view',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const USER: Permission[] = [
  'organisations.view',
  'sites.view',
  'chargers.view',
  'drivers.view',
  'sessions.view',
  'reports.view',
  'reports.export',
];

const MANAGER: Permission[] = [
  ...USER,
  'chargers.details',
  'chargers.query',
  'drivers.edit',
  'cards.edit',
  'billing.view',
  'users.view',
];

const ADMIN: Permission[] = [...PERMISSIONS];

export const ROLE_PERMISSIONS: Record<Role, ReadonlySet<Permission>> = {
  user: new Set(USER),
  manager: new Set(MANAGER),
  admin: new Set(ADMIN),
};

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].has(permission);
}

/** Admins see every organisation; everyone else is limited to their own. */
export function isGlobalRole(role: Role): boolean {
  return role === 'admin';
}
